import { yupResolver } from '@hookform/resolvers/yup';
import { EmployeePromotionModel } from 'api-hooks/employee';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ROLE,
  SIZE,
} from 'baseui/modal';
import { EnumList } from 'common/constants/enum';
import formSetErrors from 'common/helpers/form-setError';
import Separator from 'components/common/separator';
import { Form, Input } from 'components/elements';
import { Button } from 'components/elements/buttons';
import EnumSelectInput from 'legacy-modules/master/select-input/enum';
import { DialogCommonStyles } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useForm } from 'react-hook-form';

import {
  EmployeePromotionFormSchema,
  EmployeePromotionFormType,
} from './form-type';

interface EmployeePromotionFormProps {
  onSubmit: (values: EmployeePromotionFormType) => Promise<void>;
  promotion?: EmployeePromotionModel;
  onClose: () => void;
}

export default function EmployeePromotionForm(
  props: EmployeePromotionFormProps,
) {
  const { t } = useTranslation();
  const { promotion } = props;
  const defaultValues = React.useMemo<EmployeePromotionFormType>(() => {
    return {
      jobLevel: promotion?.jobLevel || '',
      promotedAt: promotion?.promotedAt || new Date(),
      promotion,
    };
  }, [promotion]);

  const resolver = yupResolver(EmployeePromotionFormSchema());

  const methods = useForm({
    resolver,
    defaultValues,
  });

  const onSubmit = React.useCallback(
    async (values: EmployeePromotionFormType) => {
      try {
        await props.onSubmit(values);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
      }
    },
    [methods.setError, props],
  );

  return (
    <Modal
      closeable={false}
      isOpen
      animate
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      size={SIZE.default}
      role={ROLE.dialog}
      overrides={DialogCommonStyles}
    >
      <ModalHeader>{t('common:promotion_job_level')}</ModalHeader>
      <Form methods={methods} onSubmit={onSubmit}>
        <ModalBody>
          <EnumSelectInput
            enumType={EnumList.EmployeeJobLevel}
            label={t('common:job_level')}
            placeholder={t('common:job_level')}
            name="jobLevel"
          />
          <Input
            type="date-time"
            name="promotedAt"
            label={t('common:promoted_at')}
            placeholder={t('common:promoted_at')}
          />
        </ModalBody>
        <ModalFooter
          style={{
            display: 'flex',
            justifyContent: 'right',
          }}
        >
          <Button
            buttonType="tertiary"
            onClick={props.onClose}
            disabled={methods.formState.isSubmitting}
          >
            {t('common:cancel')}
          </Button>
          <Separator gap={8} />

          <Input type="submit" hideIcon />
        </ModalFooter>
      </Form>
    </Modal>
  );
}
